import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/auth";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, resendVerificationEmail } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  // Check if we have verification parameters in the URL
  const id = searchParams.get('id');
  const hash = searchParams.get('hash');

  useEffect(() => {
    // If we have verification parameters, automatically verify
    if (id && hash) {
      handleVerifyFromUrl(id, hash);
    }
  }, [id, hash]);

  const handleVerifyFromUrl = async (verificationId: string, verificationHash: string) => {
    setIsVerifying(true);
    setMessage(null);
    
    try {
      await authService.verifyEmail(verificationId, verificationHash);
      setMessage("Email verified successfully! You can now log in to your account.");
      setMessageType('success');
      setIsVerified(true);
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      setMessage(error.message || "Email verification failed. Please try again.");
      setMessageType('error');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResending(true);
    setMessage(null);
    
    try {
      await resendVerificationEmail();
      setMessage("Verification email sent! Please check your inbox.");
      setMessageType('success');
    } catch (error: any) {
      setMessage(error.message || "Failed to resend verification email. Please try again.");
      setMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/login');
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // If user is not authenticated, redirect to login
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="bg-sauna-beige min-h-screen flex items-center justify-center px-4">
      <Helmet>
        <title>Verify Email | SaunaDip</title>
        <meta name="description" content="Verify your email to activate your SaunaDip account." />
      </Helmet>
      <div className="w-full max-w-md shadow-2xl border-none rounded-3xl p-0 sm:p-2 bg-white/90">
        <div className="flex flex-col items-center pt-10 pb-6">
          <div className="rounded-full bg-gradient-to-b from-sauna-orange/80 to-sauna-orange p-5 mb-6 shadow-lg flex items-center justify-center mx-auto w-fit">
            {isVerified ? (
              <CheckCircle className="h-12 w-12 text-white" />
            ) : (
              <Mail className="h-12 w-12 text-white" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-sauna-navy mb-1 text-center">
            {isVerified ? "Email Verified!" : "Verify your email"}
          </h1>
          <div className="text-lg text-sauna-navy/80 mb-4 text-center font-medium">
            {isVerified ? (
              "Your email has been successfully verified. You can now access all features of SaunaDip."
            ) : (
              <>
                We've sent a verification email to <b>{user.email}</b>
                <br />
                Please check your inbox and click the verification link.
              </>
            )}
          </div>
        </div>
        <div className="px-6 pb-8 pt-0">
          {isVerified ? (
            <div className="space-y-6">
              <div className="text-center">
                <Button
                  onClick={handleGoToLogin}
                  className="w-full bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white text-xl py-3 rounded-xl shadow-md hover:from-sauna-orange/90 hover:to-sauna-orange transition font-bold tracking-wide"
                >
                  Continue to Login
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-sauna-navy/60 mb-4">
                  Didn't receive the email? Check your spam folder or resend the verification email.
                </p>
                <Button
                  onClick={handleResend}
                  disabled={isResending}
                  className="w-full bg-gradient-to-r from-sauna-blue to-sauna-blue/80 text-white text-lg py-3 rounded-xl shadow-md hover:from-sauna-blue/90 hover:to-sauna-blue transition font-bold tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isResending ? "Sending..." : "Resend verification email"}
                </Button>
              </div>
              
              <div className="text-center">
                <a
                  href="#"
                  onClick={handleGoBack}
                  className="text-sauna-orange font-semibold hover:underline text-base"
                  tabIndex={0}
                >
                  Back to login
                </a>
              </div>
            </div>
          )}
          
          {message && (
            <div className={`text-center mt-4 p-3 rounded-lg ${
              messageType === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <div className="flex items-center justify-center gap-2">
                {messageType === 'success' ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                {message}
              </div>
            </div>
          )}
          
          <div className="text-xs text-center text-sauna-navy/60 mt-8">
            By signing up, you agree to our {" "}
            <a href="/terms-and-conditions" className="text-sauna-orange underline">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail; 